import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import {
    Distance,
    GeographicCoordinates,
    LogisticsSiteMethod,
    ShippingMethod,
} from '@lune-climate/lune'
import { LuneTheme } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import { Map as ReactGlMap } from 'mapbox-gl'
import { FC, ReactNode, useEffect, useMemo, useState } from 'react'
import ReactMapGL, { Layer, Marker, Source } from 'react-map-gl'

import { getDistanceToKM } from '../../utils/utils'
import LegMethodIcon from '../emissionsCalculationExplanation/LegMethodIcon'

import {
    getActiveMapRoute,
    getConnectionMarkerCoordinates,
    getRouteLegIndex,
    getRouteMarkerCoordinates,
} from './mapWithRouteState'
import { coordinatesToMapboxPosition, routeWithIntermediatePoints } from './mapWithRouteUtils'

import 'mapbox-gl/dist/mapbox-gl.css'

const { palette } = LuneTheme

const routeTypes = ['real', 'representative'] as const
export type RouteType = (typeof routeTypes)[number]

const RouteTypeToLine: Record<RouteType, [number, number]> = {
    real: [1, 0], // solid
    representative: [1, 2], // dashed
}

export interface AnimatedMapMarker {
    title: string
    subtitle: string
    iconSrc: string
    lat: number
    lng: number
    active?: boolean
}

const MapMarker: FC<{
    type: 'source' | 'destination' | 'connection'
    label?: string
    latitude: number
    longitude: number
    /**
     * react-map-gl requires that Source component's id doesn't change (once an instance
     * is created with it we need to keep using the same id).
     *
     * To ensure that we need to pass it from outside.
     *
     * The id has to be unique between instances.
     *
     * https://linear.app/lune/issue/LUN-5122/viewing-estimate-leg-crashes-dashboard
     */
    sourceId: string
}> = ({ label, latitude, longitude, type, sourceId }) => {
    const isDest = type === 'destination'

    // Unique id is required per marker so layers/sources don't collide
    // Technically there might be collisions in ids but the map handles
    // it somewhat gracefully: duplicate ids are not drawn but everything
    // else is rendered as normal.
    const id = `${sourceId}-marker`

    const fill = palette.Blue600
    const stroke = palette.White
    const size = 5

    return (
        <>
            <Source
                id={sourceId}
                type="geojson"
                data={{
                    type: 'FeatureCollection',
                    features: [
                        {
                            type: 'Feature',
                            geometry: { type: 'Point', coordinates: [longitude, latitude] },
                            properties: {
                                fill,
                                stroke,
                                size,
                            },
                        },
                    ],
                }}
            />

            <Layer
                id={id}
                type="circle"
                source={sourceId}
                paint={{
                    'circle-color': ['get', 'fill'],
                    'circle-stroke-color': ['get', 'stroke'],
                    'circle-stroke-width': 2,
                    'circle-radius': ['get', 'size'],
                }}
            />

            {label && (
                <Marker latitude={latitude} longitude={longitude} anchor="bottom-left">
                    <Box
                        sx={{
                            '&:before': {
                                content: `"${label}"`,
                                width: 'max-content',
                                background: palette.Blue600,
                                borderRadius: '4px',
                                borderBottomLeftRadius: '0',
                                color: 'white',
                                opacity: 1,
                                fontSize: '12px',
                                maxWidth: '200px',
                                padding: '6px',
                                position: 'absolute',
                                right: '0px',
                                margin: 'auto',
                                zIndex: '-1',
                                bottom: '10px',
                                left: '10px',
                                ...(isDest && {
                                    willChange: 'transform',
                                    transform: 'translate(-110%, 90%)',
                                    left: '0',
                                    bottom: '0',
                                    borderBottomLeftRadius: '4px',
                                    borderTopRightRadius: '0',
                                }),
                            },
                        }}
                    />
                </Marker>
            )}
        </>
    )
}

const MidPointMarker: FC<{
    label: ReactNode
    latitude: number
    longitude: number
}> = ({ label, latitude, longitude }) => {
    return (
        <Marker anchor="center" latitude={latitude} longitude={longitude}>
            <Box
                sx={{
                    width: '20px',
                    height: '20px',
                    minWidth: '20px',
                    minHeight: '20px',
                    backgroundColor: palette.Blue600,
                    borderRadius: '100%',
                    border: '2px solid white',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 0,
                    svg: {
                        width: '14px',
                        height: '14px',
                        fontSize: '14px',
                        color: 'white',
                        display: 'block',
                        verticalAlign: 'middle',
                    },
                }}
            >
                {label}
            </Box>
        </Marker>
    )
}

export interface MapWithRouteProps {
    mapboxAccessToken: string
    sourceLabel: string
    destinationLabel: string
    /**
     * Each route has to contain at least two coordinates.
     *
     * Providing fewer is a programming error and will result in an exception.
     */
    routes: MapRoute[]
    distance: Distance
    requestMethod?: ShippingMethod | LogisticsSiteMethod | null
    drawIntermediatePoints: boolean
    activeLegIndex?: number | null
    emphasizeActiveLeg?: boolean
    showMethodIconOnlyForActiveLeg?: boolean
    connectionMarkerMode?: 'waypoints' | 'route-endpoints'
    hideConnectionMarkersWhenActiveLegSelected?: boolean
}

export interface MapRoute {
    type: RouteType
    coordinates: GeographicCoordinates[]
    legIndex?: number
    sourceLabel?: string | null
    destinationLabel?: string | null
    method?: ShippingMethod | LogisticsSiteMethod | null
}
// This component still has a high branch count because it coordinates a single
// set of route-derived values across marker placement, active-leg emphasis,
// displayed labels, and viewport updates. The pure selection and marker
// deduplication logic is extracted into mapWithRouteState.ts, while the
// remaining Mapbox wiring is intentionally kept together here to avoid
// splitting tightly coupled render state across multiple helpers.
// eslint-disable-next-line complexity
export const MapWithRoute: FC<MapWithRouteProps> = ({
    mapboxAccessToken,
    sourceLabel,
    destinationLabel,
    routes,
    distance,
    requestMethod,
    drawIntermediatePoints,
    activeLegIndex,
    emphasizeActiveLeg = false,
    showMethodIconOnlyForActiveLeg = false,
    connectionMarkerMode,
    hideConnectionMarkersWhenActiveLegSelected = false,
}) => {
    useLingui()

    const [mapElement, setMapElement] = useState<ReactGlMap>()
    const [mapLoaded, setMapLoaded] = useState<boolean>(false)
    const { palette } = LuneTheme

    if (routes.length < 1 || routes.some((route) => route.coordinates.length < 2)) {
        throw new Error(`All routes must have at least two coordinates`)
    }

    // A method to calculate zoom level based on route distance
    // Generated by chatGPT
    const calculatedZoom = useMemo((): number => {
        const distanceToKm = getDistanceToKM(distance).toNumber()
        const a: number = 10 / (Math.log(4) - Math.log(1000))
        const b: number = 14 - a * Math.log(4)

        const rawZoom: number = a * Math.log(distanceToKm) + b
        return Math.max(0, Math.min(20, rawZoom))
    }, [distance])

    const transformRoute = (route: MapRoute) =>
        routeWithIntermediatePoints(route.coordinates).map(coordinatesToMapboxPosition)

    const getBounds = (coordinates: [number, number][]): [[number, number], [number, number]] => {
        const [firstLon, firstLat] = coordinates[0]

        return coordinates.slice(1).reduce<[[number, number], [number, number]]>(
            (bounds, [lon, lat]) => {
                const [[minLon, minLat], [maxLon, maxLat]] = bounds

                return [
                    [Math.min(minLon, lon), Math.min(minLat, lat)],
                    [Math.max(maxLon, lon), Math.max(maxLat, lat)],
                ]
            },
            [
                [firstLon, firstLat],
                [firstLon, firstLat],
            ],
        )
    }

    const activeRoute = getActiveMapRoute({
        routes,
        activeLegIndex,
        emphasizeActiveLeg,
    })
    const hasActiveRoute = activeRoute !== null

    const routesWithMapData = routes.map((route, routeIndex) => {
        const lineCoordinates = transformRoute(route)
        const markerCoordinates = getRouteMarkerCoordinates(route.coordinates)
        const routeLegIndex = getRouteLegIndex(routeIndex, route)
        const isActive = hasActiveRoute && routeLegIndex === activeLegIndex

        return {
            route,
            lineCoordinates,
            markerCoordinates,
            isActive,
            isDimmed: hasActiveRoute && !isActive,
        }
    })

    const activeRouteData = routesWithMapData.find((routeData) => routeData.isActive) ?? null
    const activeRouteCoordinates = activeRouteData?.lineCoordinates ?? null

    const routeByTypes: Record<
        RouteType,
        {
            coordinates: ReturnType<typeof transformRoute>
            isActive: boolean
            isDimmed: boolean
        }[]
    > = {
        real: [],
        representative: [],
    }
    // In order to determine the middle point of the journey we need to flatten all routes
    const allCoords: [number, number][] = []
    routesWithMapData.forEach((routeData) => {
        allCoords.push(...routeData.lineCoordinates)

        routeByTypes[routeData.route.type].push({
            coordinates: routeData.lineCoordinates,
            isActive: routeData.isActive,
            isDimmed: routeData.isDimmed,
        })
    })

    const middleIndex = Math.floor(allCoords.length / 2)
    const middlePoint = { lat: allCoords[middleIndex][1], lon: allCoords[middleIndex][0] }
    const iconRouteCoordinates = activeRouteCoordinates ?? allCoords
    const iconMarkerPosition = iconRouteCoordinates[Math.floor(iconRouteCoordinates.length / 2)]
    const sourceMarkerPosition =
        activeRouteData?.markerCoordinates[0] ?? routesWithMapData[0].markerCoordinates[0]
    const destinationMarkerPosition =
        activeRouteData?.markerCoordinates.at(-1) ??
        routesWithMapData.at(-1)!.markerCoordinates.at(-1)!
    const connectionMarkerRoutes =
        hasActiveRoute && hideConnectionMarkersWhenActiveLegSelected
            ? []
            : routesWithMapData.map((routeData) => routeData.markerCoordinates)
    const effectiveConnectionMarkerMode =
        connectionMarkerMode ?? (drawIntermediatePoints ? 'waypoints' : 'route-endpoints')
    const connectionMarkers = getConnectionMarkerCoordinates({
        routes: connectionMarkerRoutes,
        markerMode: effectiveConnectionMarkerMode,
        excludeCoordinates: [sourceMarkerPosition, destinationMarkerPosition],
    })
    const displayedSourceLabel = activeRoute?.sourceLabel ?? sourceLabel
    const displayedDestinationLabel = activeRoute?.destinationLabel ?? destinationLabel
    const displayedMethod = activeRoute?.method ?? requestMethod ?? null
    const shouldShowMethodIcon =
        displayedMethod != null && (hasActiveRoute || !showMethodIconOnlyForActiveLeg)

    useEffect(() => {
        if (mapElement) {
            for (const routeType of routeTypes) {
                const routeName = `route-${routeType}`
                // In multi-leg shipping estimates we're effectively reusing the same map component
                // (to some extent) between multiple legs.
                //
                // That means that when switching between legs we need to clean up the previous leg's
                // data before adding our own.
                //
                // If we just call addSource() unconditionally the application will crash.
                //
                // We have to silence eslint too because the types are wrong and getSource()
                // can return undefined.
                //
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                if (mapElement.getSource(routeName) !== undefined) {
                    // The layer has to be removed before the source as the layer uses the source
                    // and we'd get an exception otherwise.
                    mapElement.removeLayer(routeName)
                    mapElement.removeSource(routeName)
                }

                mapElement.addSource(routeName, {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: routeByTypes[routeType].map((route) => ({
                            type: 'Feature',
                            properties: {
                                routeType,
                                isActive: route.isActive,
                                isDimmed: route.isDimmed,
                            },
                            geometry: {
                                type: 'LineString',
                                coordinates: route.coordinates,
                            },
                        })),
                    },
                })

                mapElement.addLayer({
                    id: routeName,
                    type: 'line',
                    source: routeName,
                    filter: ['==', ['get', 'routeType'], routeType],
                    layout: {
                        'line-join': 'round',
                        'line-cap': 'round',
                    },
                    paint: {
                        'line-color': [
                            'case',
                            ['boolean', ['get', 'isDimmed'], false],
                            palette.Blue200,
                            palette.Blue600,
                        ],
                        'line-width': ['case', ['boolean', ['get', 'isActive'], false], 5, 3],
                        'line-opacity': ['case', ['boolean', ['get', 'isDimmed'], false], 0.65, 1],
                        'line-dasharray': RouteTypeToLine[routeType],
                    },
                })
            }

            if (activeRouteCoordinates) {
                mapElement.fitBounds(getBounds(activeRouteCoordinates), {
                    padding: 80,
                    duration: 1000,
                    maxZoom: 12,
                })
            } else {
                mapElement.flyTo({
                    zoom: calculatedZoom,
                    center: [middlePoint.lon, middlePoint.lat],
                })
            }
        }
    }, [
        activeLegIndex,
        activeRouteCoordinates,
        calculatedZoom,
        emphasizeActiveLeg,
        mapElement,
        middlePoint,
        routes,
    ])

    return (
        <Box
            sx={{
                height: '500px',
                width: '100%',
                overflow: 'hidden',
                position: 'relative',
                '.mapboxgl-canvas': {
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 'absolute',
                    width: '100%',
                    height: '100%',
                },
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    background: !mapLoaded ? palette.Grey300 : 'transparent',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    opacity: !mapLoaded ? 1 : 0,
                    transition: 'all .3s ease-in',
                }}
            />
            <ReactMapGL
                onLoad={(map) => {
                    setMapElement(map.target)
                    setMapLoaded(map.target.loaded())
                }}
                style={{
                    height: '100%',
                    width: '100%',
                    opacity: mapLoaded ? 1 : 0,
                    transition: 'opacity .3s cubic-bezier(0.25, 0.1, 0.25, 1)',
                    borderRadius: '32px',
                }}
                mapboxAccessToken={mapboxAccessToken}
                mapStyle={{
                    version: 8,
                    sources: {
                        openmaptiles: {
                            type: 'vector',
                            url: 'mapbox://mapbox.mapbox-streets-v8',
                        },
                    },
                    layers: [
                        {
                            id: 'background',
                            type: 'background',
                            paint: {
                                'background-color': '#f7fbff',
                            },
                        },
                        {
                            id: 'water',
                            type: 'fill',
                            source: 'openmaptiles',
                            'source-layer': 'water',
                            paint: {
                                'fill-color': '#d7e6ff',
                            },
                        },
                        {
                            id: 'country-borders',
                            type: 'line',
                            source: 'openmaptiles',
                            'source-layer': 'admin',
                            paint: {
                                'line-color': '#d7e6ff',
                                'line-width': 2,
                            },
                        },
                    ],
                }}
                minZoom={1}
                maxZoom={20}
                initialViewState={{
                    zoom: calculatedZoom,
                    longitude: middlePoint.lon,
                    latitude: middlePoint.lat,
                }}
            >
                {connectionMarkers.map(([longitude, latitude]) => (
                    <MapMarker
                        key={`${longitude}-${latitude}`}
                        sourceId={`connection-${longitude}-${latitude}`}
                        type="connection"
                        latitude={latitude}
                        longitude={longitude}
                    />
                ))}
                <MapMarker
                    type={'source'}
                    sourceId="source"
                    latitude={sourceMarkerPosition[1]}
                    longitude={sourceMarkerPosition[0]}
                    label={t`From ${displayedSourceLabel}`}
                />
                <MapMarker
                    type={'destination'}
                    sourceId="destination"
                    latitude={destinationMarkerPosition[1]}
                    longitude={destinationMarkerPosition[0]}
                    label={t`To ${displayedDestinationLabel}`}
                />
                {shouldShowMethodIcon && (
                    <MidPointMarker
                        latitude={iconMarkerPosition[1]}
                        longitude={iconMarkerPosition[0]}
                        label={<LegMethodIcon method={displayedMethod} />}
                    />
                )}
            </ReactMapGL>
        </Box>
    )
}

export default MapWithRoute
